import React, { useState, useMemo, useEffect, useRef } from "react";
import { Helmet } from "react-helmet-async";
import Footer from "../components/Footer";
import ShareButtons from "../components/ShareButtons";

// ─── TODO: Scale to all 23 AWS categories ────────────────────────────────────
// Current structure covers 10 priority categories (FinOps-relevant).
// AWS has 23 high-level categories — skip Blockchain, Quantum, Satellite,
// Game Development (no cost optimization angle).
// Future: add Media Services, IoT, End User Computing, Migration & Transfer.
// ─────────────────────────────────────────────────────────────────────────────

const FONT = '"Plus Jakarta Sans", system-ui, sans-serif';

function useIsMobile() {
  const [isMobile, setIsMobile] = React.useState(() => typeof window !== 'undefined' && window.innerWidth < 768);
  React.useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, []);
  return isMobile;
}

function IconCompute() {
  return (
    <svg width="18" height="18" viewBox="0 0 40 40" fill="none">
      <rect x="6" y="6" width="28" height="28" rx="4" stroke="currentColor" strokeWidth="2.5" />
      <rect x="12" y="12" width="16" height="16" rx="2" fill="currentColor" opacity="0.25" />
      <line x1="20" y1="2" x2="20" y2="6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
      <line x1="20" y1="34" x2="20" y2="38" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
      <line x1="2" y1="20" x2="6" y2="20" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
      <line x1="34" y1="20" x2="38" y2="20" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
    </svg>
  );
}
function IconStorage() {
  return (
    <svg width="18" height="18" viewBox="0 0 40 40" fill="none">
      <ellipse cx="20" cy="10" rx="14" ry="5" stroke="currentColor" strokeWidth="2.5" />
      <path d="M6 10v20c0 2.76 6.27 5 14 5s14-2.24 14-5V10" stroke="currentColor" strokeWidth="2.5" />
      <path d="M6 20c0 2.76 6.27 5 14 5s14-2.24 14-5" stroke="currentColor" strokeWidth="2" opacity="0.5" />
    </svg>
  );
}
function IconDatabase() {
  return (
    <svg width="18" height="18" viewBox="0 0 40 40" fill="none">
      <ellipse cx="20" cy="10" rx="13" ry="4.5" stroke="currentColor" strokeWidth="2.5" />
      <ellipse cx="20" cy="20" rx="13" ry="4.5" stroke="currentColor" strokeWidth="2.5" />
      <ellipse cx="20" cy="30" rx="13" ry="4.5" stroke="currentColor" strokeWidth="2.5" />
      <line x1="7" y1="10" x2="7" y2="30" stroke="currentColor" strokeWidth="2.5" />
      <line x1="33" y1="10" x2="33" y2="30" stroke="currentColor" strokeWidth="2.5" />
    </svg>
  );
}
function IconNetwork() {
  return (
    <svg width="18" height="18" viewBox="0 0 40 40" fill="none">
      <circle cx="20" cy="20" r="13" stroke="currentColor" strokeWidth="2.5" />
      <ellipse cx="20" cy="20" rx="6" ry="13" stroke="currentColor" strokeWidth="2" opacity="0.6" />
      <line x1="7" y1="20" x2="33" y2="20" stroke="currentColor" strokeWidth="2" opacity="0.6" />
      <line x1="9" y1="13" x2="31" y2="13" stroke="currentColor" strokeWidth="1.5" opacity="0.4" />
      <line x1="9" y1="27" x2="31" y2="27" stroke="currentColor" strokeWidth="1.5" opacity="0.4" />
    </svg>
  );
}
function IconServerless() {
  return (
    <svg width="18" height="18" viewBox="0 0 40 40" fill="none">
      <rect x="4" y="4" width="32" height="32" rx="6" stroke="currentColor" strokeWidth="2.5" />
      <path d="M11 30 L17 14 L20 21 L26 14 L29 30" stroke="currentColor" strokeWidth="2.5" strokeLinejoin="round" strokeLinecap="round" fill="none" />
    </svg>
  );
}
function IconGovernance() {
  return (
    <svg width="18" height="18" viewBox="0 0 40 40" fill="none">
      <rect x="4" y="4" width="32" height="32" rx="4" stroke="currentColor" strokeWidth="2.5" />
      <rect x="10" y="22" width="5" height="10" rx="1.5" fill="currentColor" opacity="0.7" />
      <rect x="17.5" y="16" width="5" height="16" rx="1.5" fill="currentColor" opacity="0.7" />
      <rect x="25" y="10" width="5" height="22" rx="1.5" fill="currentColor" opacity="0.7" />
    </svg>
  );
}
function IconAnalytics() {
  return (
    <svg width="18" height="18" viewBox="0 0 40 40" fill="none">
      <polyline points="4,32 12,20 20,26 28,12 36,18" stroke="currentColor" strokeWidth="2.5" strokeLinejoin="round" strokeLinecap="round" fill="none" />
      <circle cx="12" cy="20" r="2.5" fill="currentColor" />
      <circle cx="20" cy="26" r="2.5" fill="currentColor" />
      <circle cx="28" cy="12" r="2.5" fill="currentColor" />
    </svg>
  );
}
function IconML() {
  return (
    <svg width="18" height="18" viewBox="0 0 40 40" fill="none">
      <circle cx="20" cy="20" r="5" stroke="currentColor" strokeWidth="2.5" />
      <circle cx="8" cy="10" r="3" stroke="currentColor" strokeWidth="2" />
      <circle cx="32" cy="10" r="3" stroke="currentColor" strokeWidth="2" />
      <circle cx="8" cy="30" r="3" stroke="currentColor" strokeWidth="2" />
      <circle cx="32" cy="30" r="3" stroke="currentColor" strokeWidth="2" />
      <line x1="11" y1="12" x2="16" y2="16" stroke="currentColor" strokeWidth="1.5" opacity="0.6" />
      <line x1="29" y1="12" x2="24" y2="16" stroke="currentColor" strokeWidth="1.5" opacity="0.6" />
      <line x1="11" y1="28" x2="16" y2="24" stroke="currentColor" strokeWidth="1.5" opacity="0.6" />
      <line x1="29" y1="28" x2="24" y2="24" stroke="currentColor" strokeWidth="1.5" opacity="0.6" />
    </svg>
  );
}
function IconSecurity() {
  return (
    <svg width="18" height="18" viewBox="0 0 40 40" fill="none">
      <path d="M20 4 L34 10 L34 22 C34 30 20 36 20 36 C20 36 6 30 6 22 L6 10 Z" stroke="currentColor" strokeWidth="2.5" strokeLinejoin="round" fill="none" />
      <path d="M14 20 L18 24 L26 16" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}
function IconContainers() {
  return (
    <svg width="18" height="18" viewBox="0 0 40 40" fill="none">
      <rect x="4" y="14" width="32" height="12" rx="3" stroke="currentColor" strokeWidth="2.5" />
      <rect x="4" y="4" width="32" height="8" rx="3" stroke="currentColor" strokeWidth="2" opacity="0.5" />
      <rect x="4" y="28" width="32" height="8" rx="3" stroke="currentColor" strokeWidth="2" opacity="0.5" />
      <line x1="14" y1="14" x2="14" y2="26" stroke="currentColor" strokeWidth="1.5" opacity="0.4" />
      <line x1="26" y1="14" x2="26" y2="26" stroke="currentColor" strokeWidth="1.5" opacity="0.4" />
    </svg>
  );
}
function IconAppIntegration() {
  return (
    <svg width="18" height="18" viewBox="0 0 40 40" fill="none">
      <circle cx="10" cy="20" r="5" stroke="currentColor" strokeWidth="2.5" />
      <circle cx="30" cy="10" r="5" stroke="currentColor" strokeWidth="2.5" />
      <circle cx="30" cy="30" r="5" stroke="currentColor" strokeWidth="2.5" />
      <line x1="15" y1="18" x2="25" y2="12" stroke="currentColor" strokeWidth="2" opacity="0.6" />
      <line x1="15" y1="22" x2="25" y2="28" stroke="currentColor" strokeWidth="2" opacity="0.6" />
    </svg>
  );
}

interface Tip {
  title: string;
  savings: string;
  effort: "Low" | "Medium" | "High";
  description: string;
  action: string;
  awsDocsUrl?: string;
}

interface Service {
  id: string;
  label: string;
  tips: Tip[];
}

interface Category {
  id: string;
  label: string;
  color: string;
  Icon: () => React.ReactElement;
  services: Service[];
}

const categories: Category[] = [
  {
    id: "compute",
    label: "Compute",
    color: "#FF9900",
    Icon: IconCompute,
    services: [
      {
        id: "ec2",
        label: "EC2",
        tips: [
          { title: "Right-size instances", savings: "Up to 70%", effort: "Medium", description: "Use AWS Compute Optimizer or Cost Explorer to identify over-provisioned EC2s. Downsize m5.2xlarge → m5.xlarge if CPU stays <40%.", action: "Check Compute Optimizer → 'Right sizing recommendations'", awsDocsUrl: "https://docs.aws.amazon.com/compute-optimizer/latest/ug/view-ec2-recommendations.html" },
          { title: "Reserved Instances", savings: "Up to 72%", effort: "Low", description: "Commit to 1- or 3-year terms for stable workloads. Standard RIs give deeper discounts; Convertible RIs give flexibility to change instance family.", action: "EC2 → Reserved Instances → Purchase" },
          { title: "Savings Plans", savings: "Up to 66%", effort: "Low", description: "More flexible than RIs. Compute Savings Plans apply across EC2, Fargate, and Lambda. Commit to $/hr spend, not specific instance types.", action: "Cost Explorer → Savings Plans → Purchase" },
          { title: "Spot Instances", savings: "Up to 90%", effort: "High", description: "Use for fault-tolerant, stateless, or batch workloads (ML training, CI/CD, data processing). Mix Spot + On-Demand with Auto Scaling groups.", action: "Use Spot Fleet or EC2 Auto Scaling with mixed instance policy" },
          { title: "Stop idle instances", savings: "100% of idle cost", effort: "Low", description: "Schedule dev/test instances to stop nights & weekends. A t3.medium running 24/7 vs 9-5 weekdays saves ~70% of its cost.", action: "Use EC2 Instance Scheduler or Lambda + EventBridge rules" },
          { title: "Graviton (ARM) instances", savings: "Up to 40%", effort: "Medium", description: "Graviton3 (e.g. m7g, c7g) offers better price-performance than x86. Most modern runtimes (Java, Python, Node) work out of the box.", action: "Change instance type to g-family equivalents (m5 → m7g)" },
        ],
      },
      {
        id: "auto-scaling",
        label: "Auto Scaling",
        tips: [
          { title: "Use predictive scaling", savings: "20–40%", effort: "Low", description: "Predictive Scaling uses ML to forecast load and scale before demand hits. Reduces over-provisioning from reactive scaling buffers.", action: "EC2 Auto Scaling → Scaling policies → Add predictive scaling policy" },
          { title: "Set aggressive scale-in policies", savings: "15–30%", effort: "Low", description: "Default scale-in is conservative. Most apps can tolerate faster scale-in. Tighten cooldown periods and lower CPU thresholds to reclaim capacity sooner.", action: "Auto Scaling → Scaling policies → reduce scale-in cooldown to 120s" },
          { title: "Use target tracking over step scaling", savings: "10–25%", effort: "Low", description: "Target tracking (e.g. keep CPU at 60%) self-tunes continuously. Step scaling requires manual tuning and tends to over-provision headroom.", action: "Replace step scaling policies with target tracking on key metrics" },
        ],
      },
    ],
  },
  {
    id: "containers",
    label: "Containers",
    color: "#FF9900",
    Icon: IconContainers,
    services: [
      {
        id: "ecs-fargate",
        label: "ECS / Fargate",
        tips: [
          { title: "Use Fargate Spot", savings: "Up to 70%", effort: "Medium", description: "Fargate Spot runs tasks on spare AWS capacity at a steep discount. Ideal for batch jobs, background workers, and interruptible workloads.", action: "ECS Task Definition → Capacity provider → FARGATE_SPOT" },
          { title: "Right-size task CPU & memory", savings: "20–50%", effort: "Low", description: "Fargate bills per vCPU-second and GB-second. Many services over-provision by 2x. Profile with CloudWatch Container Insights before sizing.", action: "ECS → Task definition → Update CPU/memory to p95 utilization" },
          { title: "Compute Savings Plans for Fargate", savings: "Up to 52%", effort: "Low", description: "Compute Savings Plans cover Fargate alongside EC2 and Lambda. Commit to $/hr usage for consistent Fargate workloads.", action: "Cost Explorer → Savings Plans → Compute Savings Plan" },
          { title: "Disable Container Insights on non-prod", savings: "Up to $50/cluster/month", effort: "Low", description: "Container Insights costs ~$0.50 per cluster per hour. Enable only for production clusters; use basic CloudWatch metrics for dev/staging.", action: "ECS → Cluster → Update → disable Container Insights on non-prod" },
        ],
      },
      {
        id: "eks",
        label: "EKS",
        tips: [
          { title: "Cluster autoscaler + Spot nodes", savings: "Up to 80%", effort: "High", description: "Run worker nodes on EC2 Spot using node groups or Karpenter. Mix Spot + On-Demand at 80/20 for cost with availability.", action: "Use Karpenter with Spot instance provisioner; set nodeSelector on batch workloads" },
          { title: "Consolidate small clusters", savings: "30–60%", effort: "High", description: "Each EKS cluster has a $0.10/hr control plane fee. Multiple dev clusters can often be merged into one with namespaces per team.", action: "Audit active clusters; merge dev/staging into a shared cluster with RBAC namespaces" },
          { title: "Compute Savings Plans for EKS nodes", savings: "Up to 66%", effort: "Low", description: "EKS nodes are EC2 instances — Compute Savings Plans apply. Analyze steady-state node count and commit accordingly.", action: "Cost Explorer → Savings Plans recommendations → EC2/Compute" },
          { title: "Use ECR lifecycle policies", savings: "Up to 80% on ECR storage", effort: "Low", description: "ECR charges $0.10/GB/month. Old image versions pile up fast. Lifecycle policies auto-delete untagged or old images after N days.", action: "ECR → Repository → Lifecycle policy → expire untagged images after 7 days" },
        ],
      },
      {
        id: "ecr",
        label: "ECR",
        tips: [
          { title: "ECR lifecycle policies", savings: "40–80% on image storage", effort: "Low", description: "Container image repos grow silently. Set rules to keep only the last N tagged images and expire all untagged images within 1 day.", action: "ECR → Repository → Lifecycle policy → Add rule" },
          { title: "Use ECR Public for open images", savings: "100% of pull egress", effort: "Low", description: "ECR Public Gallery offers free data transfer for publicly available images. Move public base images there to eliminate egress charges.", action: "Push to ECR Public instead of private ECR for open-source workloads" },
        ],
      },
    ],
  },
  {
    id: "storage",
    label: "Storage",
    color: "#7AA116",
    Icon: IconStorage,
    services: [
      {
        id: "s3",
        label: "S3",
        tips: [
          { title: "S3 Intelligent-Tiering", savings: "Up to 95%", effort: "Low", description: "Automatically moves objects between access tiers. No retrieval fees for frequent/infrequent tiers. Ideal for unknown or changing access patterns.", action: "S3 Lifecycle rule → Transition to Intelligent-Tiering" },
          { title: "S3 Lifecycle policies", savings: "40–80%", effort: "Low", description: "Move objects to cheaper tiers after N days: Standard → Standard-IA (30d) → Glacier Instant (90d) → Glacier Deep Archive (180d).", action: "S3 Bucket → Management → Lifecycle rules → Add rule" },
          { title: "Compress & deduplicate data", savings: "20–60%", effort: "Medium", description: "Store logs in Parquet/ORC instead of raw JSON/CSV. Compress with gzip or Snappy. Reduces both storage and Athena scan costs.", action: "Use AWS Glue or Firehose to convert formats on ingestion" },
          { title: "Enable S3 Storage Lens", savings: "Varies", effort: "Low", description: "Free dashboard showing bucket-level storage trends, object counts, and cost-optimization opportunities across all accounts.", action: "S3 → Storage Lens → Create dashboard → enable free metrics" },
          { title: "Delete incomplete multipart uploads", savings: "$5–50/month of hidden waste", effort: "Low", description: "Failed multipart uploads leave partial data that still bills at full S3 rates. A lifecycle rule cleans these up automatically.", action: "S3 → Bucket → Management → Lifecycle → expire incomplete multipart uploads after 7 days" },
        ],
      },
      {
        id: "ebs",
        label: "EBS",
        tips: [
          { title: "Migrate gp2 → gp3", savings: "Up to 20%", effort: "Low", description: "gp3 is cheaper than gp2 and gives independent IOPS/throughput control with zero downtime. There's no reason to stay on gp2.", action: "EC2 → Volumes → Modify → Change type to gp3" },
          { title: "Delete unattached volumes", savings: "100% of waste", effort: "Low", description: "Unattached EBS volumes still bill at full rate. Snapshot them first if unsure, then delete. Orphaned snapshots are also common waste.", action: "EC2 → Volumes → Filter state: 'available' → snapshot + delete" },
          { title: "Downsize over-provisioned volumes", savings: "20–60%", effort: "Medium", description: "Check CloudWatch VolumeConsumedReadWriteOps and free space. Volumes at <30% utilization are candidates for downsizing.", action: "Create snapshot → restore as smaller volume → swap attachment" },
          { title: "Delete old EBS snapshots", savings: "$0.05/GB/month recovered", effort: "Low", description: "Snapshots accumulate silently. Automated backups expire, manual ones don't. Review and purge snapshots older than your retention policy.", action: "EC2 → Snapshots → filter by date → delete or set Data Lifecycle Manager policy" },
        ],
      },
      {
        id: "efs",
        label: "EFS",
        tips: [
          { title: "Use EFS Intelligent-Tiering", savings: "Up to 92%", effort: "Low", description: "EFS IA storage is ~85% cheaper than standard. Intelligent-Tiering automatically moves files not accessed in 30 days to IA.", action: "EFS → File system → Lifecycle management → Enable IA transition" },
          { title: "Consider EBS over EFS for single-instance workloads", savings: "60–80%", effort: "Medium", description: "EFS is 3x more expensive than EBS gp3. If only one instance needs the volume, EBS is almost always cheaper.", action: "Audit which EFS mounts have >1 active EC2 client; switch singletons to EBS" },
        ],
      },
    ],
  },
  {
    id: "database",
    label: "Database",
    color: "#C7131F",
    Icon: IconDatabase,
    services: [
      {
        id: "rds",
        label: "RDS",
        tips: [
          { title: "RDS Reserved Instances", savings: "Up to 69%", effort: "Low", description: "Stable databases are perfect RI candidates. 1-year No Upfront gives ~30%; 3-year All Upfront gives ~69%. Region + engine specific.", action: "RDS → Reserved instances → Purchase" },
          { title: "Aurora Serverless v2", savings: "Up to 80% vs provisioned", effort: "Medium", description: "Scales in 0.5 ACU increments. Ideal for dev/staging or variable-traffic production. Minimum is 0.5 ACU vs always-on provisioned.", action: "Migrate RDS provisioned cluster → Aurora Serverless v2" },
          { title: "Stop dev/test RDS instances", savings: "100% compute cost", effort: "Low", description: "RDS can be stopped for up to 7 days (storage still billed). Auto-stop/start on a schedule eliminates nights & weekends waste.", action: "RDS → DB instance → Actions → Stop temporarily (automate with Lambda + EventBridge)" },
          { title: "Delete unused manual snapshots", savings: "Varies", effort: "Low", description: "Manual snapshots are retained indefinitely at $0.095/GB-month. Automated snapshots expire per retention setting — manuals don't.", action: "RDS → Snapshots → filter 'manual' → delete old ones" },
          { title: "Right-size RDS instances", savings: "30–50%", effort: "Medium", description: "Most RDS instances run at <30% CPU. Check CloudWatch DatabaseConnections and CPUUtilization before sizing.", action: "Compute Optimizer → RDS recommendations → review and modify instance class" },
        ],
      },
      {
        id: "dynamodb",
        label: "DynamoDB",
        tips: [
          { title: "Switch to provisioned + Auto Scaling", savings: "50–70%", effort: "Medium", description: "On-demand mode is ~7x more expensive per million requests. If traffic is predictable, provisioned + Application Auto Scaling is far cheaper.", action: "DynamoDB → Table → Capacity → Change to Provisioned + enable Auto Scaling" },
          { title: "Use DynamoDB Standard-IA table class", savings: "Up to 60% storage cost", effort: "Low", description: "Standard-IA cuts storage costs by 60% at a modest read/write cost increase. Great for tables with high data volume but infrequent access.", action: "DynamoDB → Table → Additional settings → Table class → Standard-IA" },
          { title: "Enable TTL on time-sensitive items", savings: "Reduces storage cost", effort: "Low", description: "DynamoDB TTL automatically deletes expired items for free. Storing stale session data, logs, or events for years is a common hidden cost.", action: "DynamoDB → Table → TTL → Enable and set expiry attribute" },
          { title: "Use DAX for read-heavy workloads", savings: "Reduce RCU costs by 60–80%", effort: "Medium", description: "DynamoDB Accelerator (DAX) caches hot reads in-memory. If your table has repetitive reads, DAX can slash RCU usage dramatically.", action: "DynamoDB → DAX → Create cluster → point app to DAX endpoint" },
        ],
      },
      {
        id: "elasticache",
        label: "ElastiCache",
        tips: [
          { title: "Reserved nodes for stable caches", savings: "Up to 55%", effort: "Low", description: "ElastiCache reserved nodes offer 30–55% savings on 1- or 3-year commitments. Stable Redis/Memcached clusters are ideal candidates.", action: "ElastiCache → Reserved cache nodes → Purchase" },
          { title: "Right-size node types", savings: "20–50%", effort: "Medium", description: "Most ElastiCache clusters are over-provisioned. Check CloudWatch EngineCPUUtilization and DatabaseMemoryUsagePercentage to find the sweet spot.", action: "ElastiCache → Metrics → identify low-utilization nodes → scale down" },
          { title: "Use Serverless ElastiCache for spiky traffic", savings: "Up to 70% vs provisioned", effort: "Medium", description: "ElastiCache Serverless (Redis OSS) scales to zero and bills per ECPUs consumed. No idle node cost for infrequent or spiky cache usage.", action: "ElastiCache → Create → Serverless cache → select Redis OSS" },
        ],
      },
    ],
  },
  {
    id: "networking",
    label: "Networking",
    color: "#8C4FFF",
    Icon: IconNetwork,
    services: [
      {
        id: "nat",
        label: "NAT Gateway",
        tips: [
          { title: "VPC Endpoints for S3 & DynamoDB", savings: "100% of NAT cost for those services", effort: "Low", description: "Traffic to S3 and DynamoDB through NAT Gateway costs $0.045/GB. VPC Gateway Endpoints are completely free. Biggest quick win on most bills.", action: "VPC → Endpoints → Create → S3 / DynamoDB (Gateway type, free)" },
          { title: "Consolidate NAT Gateways", savings: "Up to 80%", effort: "Medium", description: "Each NAT GW costs $0.045/hr + $0.045/GB. Non-prod environments rarely need per-AZ redundancy — one shared NAT GW is fine.", action: "Review VPC → NAT Gateways per account; remove duplicates in dev/staging VPCs" },
          { title: "Replace with NAT instances for low traffic", savings: "60–80%", effort: "High", description: "A t4g.nano NAT instance costs ~$3/month vs $32+/month for a NAT Gateway. Viable for VPCs with <1GB/day outbound traffic.", action: "Launch NAT instance AMI (fck-nat project) → update route tables" },
        ],
      },
      {
        id: "cloudfront",
        label: "CloudFront",
        tips: [
          { title: "Use CloudFront to reduce S3 egress", savings: "Up to 75%", effort: "Medium", description: "CloudFront data transfer out to internet is cheaper than direct S3 or EC2 egress. First 1TB/month is free. Caching also cuts origin requests.", action: "Create CloudFront distribution → set S3 as origin → enable caching" },
          { title: "Enable Origin Shield", savings: "Reduces origin load & cost", effort: "Low", description: "Origin Shield adds a caching layer between edge locations and your origin, reducing origin fetch requests (and data transfer fees) by 60–80%.", action: "CloudFront → Distribution → Origin → Enable Origin Shield" },
          { title: "Use Cache Policies aggressively", savings: "20–50% on Lambda/ALB origin cost", effort: "Low", description: "Set long TTLs for static assets and use cache invalidation on deploy — don't pay for repeated origin hits.", action: "CloudFront → Behaviors → Cache policy → set max TTL to 86400s for static paths" },
        ],
      },
      {
        id: "data-transfer",
        label: "Data Transfer",
        tips: [
          { title: "Minimize cross-AZ traffic", savings: "Up to 100% of cross-AZ fees", effort: "Medium", description: "Cross-AZ data transfer costs $0.01/GB each way. Keep app servers, databases, and caches in the same AZ for internal traffic.", action: "Use AZ-aware load balancing; pin read replicas to same AZ as app tier" },
          { title: "Use PrivateLink for internal services", savings: "Eliminates NAT/internet egress", effort: "Medium", description: "PrivateLink keeps traffic between AWS services on the AWS backbone — no NAT Gateway, no internet egress, lower latency.", action: "VPC → Endpoint services → Create PrivateLink endpoint for internal APIs" },
          { title: "Use Direct Connect for high-volume on-prem transfer", savings: "Up to 80% vs internet egress", effort: "High", description: "Direct Connect charges $0.02/GB vs $0.09/GB for internet egress at scale. Pays off at ~500GB+/month sustained transfer.", action: "AWS → Direct Connect → Order connection through a DX partner" },
        ],
      },
      {
        id: "route53",
        label: "Route 53",
        tips: [
          { title: "Consolidate hosted zones", savings: "$0.50/zone/month recovered", effort: "Low", description: "Each Route 53 hosted zone costs $0.50/month. Dev and staging subdomains don't need separate zones — use the same zone with prefixed records.", action: "Audit hosted zones → merge subdomains into parent zone" },
          { title: "Use alias records over CNAME", savings: "Eliminates per-query cost for AWS resources", effort: "Low", description: "Alias records pointing to AWS resources (ALB, CloudFront, S3) are free. CNAMEs bill at $0.40/million queries. Switch wherever possible.", action: "Route 53 → Record → Type: A → Alias: Yes → select AWS resource" },
        ],
      },
    ],
  },
  {
    id: "serverless",
    label: "Serverless",
    color: "#E7157B",
    Icon: IconServerless,
    services: [
      {
        id: "lambda",
        label: "Lambda",
        tips: [
          { title: "Tune Lambda memory with Power Tuning", savings: "Up to 50%", effort: "Low", description: "Lambda charges per GB-second. Many functions run fine at 512MB vs default 1024MB. The Power Tuning tool finds the optimal memory/cost point.", action: "Deploy 'aws-lambda-power-tuning' State Machine → test across memory sizes" },
          { title: "Use ARM (Graviton2) Lambda", savings: "20%", effort: "Low", description: "arm64 Lambda costs 20% less per GB-second and often runs faster. Supported for all major runtimes with no code changes.", action: "Lambda → Configuration → Architecture → arm64" },
          { title: "Reduce package size & cold starts", savings: "10–40%", effort: "High", description: "Shorter init = lower bill. Use lazy imports, tree-shake dependencies, keep connections warm. SnapStart eliminates Java cold start cost.", action: "Use X-Ray to profile init time; enable SnapStart for Java 11+" },
          { title: "Compute Savings Plans", savings: "Up to 17%", effort: "Low", description: "Commit to a minimum $/hr Lambda spend under Compute Savings Plans for a blanket discount on consistent usage.", action: "Cost Explorer → Savings Plans → Compute Savings Plan" },
          { title: "Set concurrency limits on non-critical functions", savings: "Prevents runaway cost spikes", effort: "Low", description: "Without reserved concurrency, a buggy function or traffic spike can consume your entire account's Lambda quota and cost thousands overnight.", action: "Lambda → Configuration → Concurrency → Reserve concurrency → set a cap" },
        ],
      },
      {
        id: "api-gateway",
        label: "API Gateway",
        tips: [
          { title: "Use HTTP API instead of REST API", savings: "Up to 71%", effort: "Medium", description: "HTTP APIs cost $1.00/million requests vs $3.50 for REST APIs. Unless you need REST-specific features (caching, usage plans), HTTP API is the right choice.", action: "Migrate REST API to HTTP API — most Lambda proxy integrations work as-is" },
          { title: "Enable caching on REST APIs", savings: "Reduces Lambda invocations", effort: "Low", description: "API Gateway caching (0.5–237GB) absorbs repeated identical requests, cutting Lambda invocations and downstream database calls.", action: "API Gateway → Stage → Cache settings → Enable + set TTL" },
        ],
      },
      {
        id: "step-functions",
        label: "Step Functions",
        tips: [
          { title: "Use Express Workflows for high-volume", savings: "Up to 99%", effort: "Medium", description: "Standard Workflows bill per state transition ($0.025/1k). Express Workflows bill per execution duration — ideal for high-frequency, short-lived workflows.", action: "For workflows <5 min and >1k/day, migrate to Express Workflow type" },
        ],
      },
    ],
  },
  {
    id: "analytics",
    label: "Analytics",
    color: "#01A88D",
    Icon: IconAnalytics,
    services: [
      {
        id: "athena",
        label: "Athena",
        tips: [
          { title: "Convert data to Parquet or ORC", savings: "Up to 87% on scan cost", effort: "Medium", description: "Athena charges $5/TB scanned. Parquet/ORC columnar formats with compression reduce scan size by 75–90% vs JSON or CSV. This is the single biggest Athena lever.", action: "Use AWS Glue ETL or Firehose to convert raw S3 data to Parquet on ingest" },
          { title: "Partition your S3 data", savings: "50–95% on scan cost", effort: "Medium", description: "Partition by date/region/account. Athena skips entire prefixes that don't match the WHERE clause — massive scan reduction for time-series queries.", action: "Add Glue table partition keys (year/month/day) and use partition projection" },
          { title: "Use Athena result reuse", savings: "Up to 100% on repeated queries", effort: "Low", description: "Athena can reuse query results for up to 7 days. Identical queries in dashboards or scheduled reports pay zero scan cost on cache hits.", action: "Athena → Workgroup → Settings → Enable query result reuse → set max age" },
          { title: "Set workgroup data scan limits", savings: "Prevents runaway scans", effort: "Low", description: "A single bad query can scan terabytes. Set per-query and per-workgroup data scan limits to cap accidental cost.", action: "Athena → Workgroups → Configure → Data usage controls → set limits" },
        ],
      },
      {
        id: "redshift",
        label: "Redshift",
        tips: [
          { title: "Use Redshift Serverless for variable workloads", savings: "40–70% vs provisioned", effort: "Medium", description: "Redshift Serverless bills per RPU-second with auto-pause. Clusters running overnight with no queries waste 100% of that compute cost.", action: "Migrate provisioned cluster → Redshift Serverless namespace" },
          { title: "Enable auto-pause on dev clusters", savings: "Up to 80% on dev spend", effort: "Low", description: "Provisioned Redshift clusters can be paused manually or on schedule. Dev/staging clusters don't need to run 24/7.", action: "Redshift → Cluster → Actions → Pause (or set EventBridge schedule)" },
          { title: "Use RA3 nodes with managed storage", savings: "Up to 50% vs DS2", effort: "Medium", description: "RA3 separates compute from storage (uses S3 under the hood). Scale storage independently without paying for compute you don't need.", action: "Redshift → Resize → Elastic resize to RA3 node types" },
          { title: "Use Redshift Spectrum for cold data", savings: "Avoids storage duplication cost", effort: "Medium", description: "Query S3 data directly via Spectrum rather than loading historical data into Redshift. Pay only for S3 storage + Spectrum scan cost.", action: "Create external schema in Redshift → query S3 via Spectrum for cold data" },
        ],
      },
      {
        id: "kinesis",
        label: "Kinesis",
        tips: [
          { title: "Right-size Kinesis Data Streams shards", savings: "30–70%", effort: "Medium", description: "Each shard costs $0.015/hr ($10.80/month) regardless of utilization. Over-provisioned shard counts are one of the most common Kinesis waste patterns.", action: "CloudWatch → GetRecords.IteratorAgeMilliseconds + IncomingBytes → reduce shard count" },
          { title: "Use Kinesis Firehose instead of custom consumers", savings: "Eliminates EC2/Lambda consumer cost", effort: "Medium", description: "Firehose handles S3/Redshift/Elasticsearch delivery with no infrastructure. Custom consumers on EC2 often cost more than the stream itself.", action: "Replace custom stream consumer with Kinesis Data Firehose delivery stream" },
          { title: "Switch to on-demand capacity mode", savings: "Up to 50% vs over-provisioned shards", effort: "Low", description: "On-demand mode auto-scales shards and bills per GB throughput. Better than paying for idle shards during off-peak hours.", action: "Kinesis → Data Stream → Configuration → Capacity mode → On-demand" },
        ],
      },
      {
        id: "emr",
        label: "EMR",
        tips: [
          { title: "Use EMR on Spot Instances", savings: "Up to 80%", effort: "Medium", description: "EMR core and task nodes are great Spot candidates. Keep only 1-2 On-Demand master nodes. EMR handles Spot interruption gracefully for batch jobs.", action: "EMR → Instance fleet → mix On-Demand master + Spot core/task nodes" },
          { title: "Use EMR Serverless for batch workloads", savings: "30–60% vs always-on clusters", effort: "Medium", description: "EMR Serverless bills per vCPU-second and GB-second, with auto-scaling and no idle cluster cost. Ideal for intermittent Spark/Hive jobs.", action: "EMR → Create application → EMR Serverless → submit jobs via API" },
          { title: "Terminate clusters after job completion", savings: "100% of idle cluster cost", effort: "Low", description: "Clusters left running after a Spark job completes are pure waste. Transient clusters (one cluster per job) are almost always cheaper than persistent clusters.", action: "Set EMR cluster → Auto-terminate → after last step completes" },
        ],
      },
    ],
  },
  {
    id: "ml",
    label: "Machine Learning",
    color: "#01A88D",
    Icon: IconML,
    services: [
      {
        id: "sagemaker",
        label: "SageMaker",
        tips: [
          { title: "Use Spot training instances", savings: "Up to 90%", effort: "Medium", description: "SageMaker Training supports managed Spot training with automatic checkpointing. ML training is the most interruptible workload on AWS — Spot is a no-brainer.", action: "SageMaker → Training job → Resource config → Enable managed Spot training" },
          { title: "Stop idle SageMaker notebooks", savings: "Up to 100% of notebook cost", effort: "Low", description: "Notebook instances cost $0.05–$4+/hr and run 24/7 by default. Auto-stop after inactivity eliminates weeknight and weekend waste.", action: "SageMaker → Notebook instances → Lifecycle config → add idle-time auto-stop script" },
          { title: "Right-size inference endpoints", savings: "30–70%", effort: "Medium", description: "Most SageMaker endpoints are sized for peak traffic they rarely see. Use multi-model endpoints or Serverless Inference for low-RPS models.", action: "Switch to Serverless Inference or use Auto Scaling on real-time endpoints" },
          { title: "Use SageMaker Savings Plans", savings: "Up to 64%", effort: "Low", description: "SageMaker Savings Plans cover training, inference, and processing. For consistent ML workloads, commit to $/hr spend for significant discounts.", action: "Cost Explorer → Savings Plans → SageMaker Savings Plans" },
          { title: "Use Serverless Inference for infrequent models", savings: "100% when idle", effort: "Medium", description: "Serverless Inference scales to zero between requests. Pay only per inference call. Perfect for models called <100 times/day.", action: "SageMaker → Endpoint → Serverless configuration → set memory + max concurrency" },
        ],
      },
      {
        id: "bedrock",
        label: "Bedrock",
        tips: [
          { title: "Cache prompt results for repetitive queries", savings: "Up to 90% on token costs", effort: "Medium", description: "If your app calls Bedrock with similar prompts repeatedly (e.g. classification, extraction), cache responses in ElastiCache or DynamoDB with TTL.", action: "Add semantic caching layer (Redis + embeddings) in front of Bedrock API calls" },
          { title: "Use Provisioned Throughput for high-volume models", savings: "Up to 50% vs on-demand", effort: "Medium", description: "Provisioned Throughput (model units) is cheaper than on-demand pricing at scale. Commit to a minimum throughput for predictable inference workloads.", action: "Bedrock → Provisioned throughput → Purchase model units for your model" },
          { title: "Choose smaller models where accuracy is sufficient", savings: "80–95% on token cost", effort: "Medium", description: "Claude Haiku costs ~20x less than Claude Opus. Use the smallest model that passes your quality bar — run evals to find the threshold.", action: "Benchmark smaller model variants on your eval dataset before defaulting to largest" },
          { title: "Set token limits aggressively", savings: "20–50% on output tokens", effort: "Low", description: "Output tokens cost 3–5x input tokens. Set max_tokens to the minimum your use case needs — don't let models ramble.", action: "Set max_tokens in your API call to the tightest reasonable value for your use case" },
        ],
      },
    ],
  },
  {
    id: "security",
    label: "Security",
    color: "#DD344C",
    Icon: IconSecurity,
    services: [
      {
        id: "guardduty",
        label: "GuardDuty",
        tips: [
          { title: "Disable unused GuardDuty features", savings: "30–60% on GuardDuty cost", effort: "Low", description: "GuardDuty has optional add-ons: S3 protection, EKS protection, Lambda protection, Malware protection. Each costs extra. Disable what you don't actively alert on.", action: "GuardDuty → Settings → Protection plans → disable unused protection types" },
          { title: "Use suppression rules to reduce noise", savings: "Indirect — reduces analyst time cost", effort: "Low", description: "GuardDuty bills per event volume. Suppressing known-good findings (e.g. internal scanners) reduces noise and can lower processed event counts.", action: "GuardDuty → Findings → Create suppression rule → filter known-good sources" },
        ],
      },
      {
        id: "waf",
        label: "WAF",
        tips: [
          { title: "Audit active WAF rules", savings: "$1–5/month per unused rule", effort: "Low", description: "AWS WAF charges $1/month per rule and $0.60/million requests. Unused rules from old managed rule groups silently accumulate cost.", action: "WAF → Web ACLs → review all rules → delete unused or redundant rules" },
          { title: "Use Bot Control only where needed", savings: "Up to $10/month per ACL", effort: "Low", description: "AWS Managed Rules Bot Control costs $10/month per ACL on top of base WAF pricing. Enable only on public-facing endpoints with real bot traffic.", action: "WAF → Web ACL → Managed rules → evaluate if Bot Control justifies cost" },
        ],
      },
      {
        id: "secrets-manager",
        label: "Secrets Manager",
        tips: [
          { title: "Consolidate secrets into fewer entries", savings: "$0.40/secret/month recovered", effort: "Medium", description: "Secrets Manager charges $0.40/month per secret. Storing individual DB credentials, API keys, etc. as separate secrets adds up. Combine related config into a single JSON secret.", action: "Merge related secrets into one JSON-structured secret → update app to parse" },
          { title: "Use SSM Parameter Store for non-sensitive config", savings: "100% for non-secrets", effort: "Low", description: "SSM Parameter Store Standard tier is free. Use it for non-sensitive configuration values instead of Secrets Manager.", action: "Move non-sensitive env vars from Secrets Manager → SSM Parameter Store Standard" },
          { title: "Delete unused secrets", savings: "$0.40/secret/month recovered", effort: "Low", description: "Abandoned secrets from deleted apps or services keep billing. Audit and purge secrets with no recent GetSecretValue calls.", action: "CloudTrail → filter GetSecretValue events → identify unreferenced secrets → delete" },
        ],
      },
      {
        id: "cloudtrail",
        label: "CloudTrail",
        tips: [
          { title: "Avoid duplicate trails", savings: "$2/100k events saved", effort: "Low", description: "Multiple trails logging the same events multiply your cost. One organization trail covering all accounts and regions is almost always enough.", action: "CloudTrail → Trails → consolidate to one org-level trail → delete duplicates" },
          { title: "Use S3 lifecycle on CloudTrail logs", savings: "40–80% on log storage", effort: "Low", description: "CloudTrail logs to S3 in Standard class by default. For compliance archives you don't actively query, move to Glacier after 90 days.", action: "S3 → CloudTrail bucket → Lifecycle rule → transition to Glacier after 90 days" },
        ],
      },
    ],
  },
  {
    id: "app-integration",
    label: "App Integration",
    color: "#E7157B",
    Icon: IconAppIntegration,
    services: [
      {
        id: "sqs",
        label: "SQS",
        tips: [
          { title: "Use long polling instead of short polling", savings: "Up to 95% on SQS requests", effort: "Low", description: "Short polling checks for messages and returns immediately — even if empty. Each empty response still costs money. Long polling (20s wait) slashes empty-receive API calls.", action: "SQS → Queue → Edit → Receive message wait time → set to 20 seconds" },
          { title: "Use batch operations", savings: "Up to 90% on request costs", effort: "Low", description: "SQS charges per API request. SendMessageBatch sends up to 10 messages per request vs 1 for single sends. Same for receives and deletes.", action: "Use SendMessageBatch, ReceiveMessage (MaxNumberOfMessages=10), DeleteMessageBatch" },
          { title: "Delete unused queues and DLQs", savings: "Eliminates polling cost", effort: "Low", description: "SQS is cheap per request, but unused queues with active consumers polling them generate constant empty-receive costs.", action: "SQS → identify queues with 0 messages and no producers → delete or disable consumers" },
        ],
      },
      {
        id: "sns",
        label: "SNS",
        tips: [
          { title: "Filter SNS subscriptions at the topic level", savings: "Up to 90% on delivery cost", effort: "Low", description: "Without filter policies, every subscriber receives every message. Filter policies push only relevant messages to each subscriber — reducing Lambda invocations and HTTP deliveries.", action: "SNS → Subscription → Edit → Subscription filter policy → add attribute filters" },
          { title: "Use SQS over HTTP subscriptions", savings: "Eliminates retry cost", effort: "Medium", description: "HTTP/HTTPS endpoints that fail trigger SNS retries (up to 100,762 attempts). SQS subscriptions are cheaper and handle backpressure gracefully.", action: "Replace HTTP SNS subscriptions with SQS queue subscriptions" },
        ],
      },
      {
        id: "eventbridge",
        label: "EventBridge",
        tips: [
          { title: "Avoid high-volume events to EventBridge", savings: "$1/million events saved", effort: "Medium", description: "EventBridge charges $1/million custom events. For very high-volume event streams (>1M/month), SQS or Kinesis is significantly cheaper.", action: "Evaluate event volume — >1M/month → switch to SQS or Kinesis for cost" },
          { title: "Use event filtering to reduce rule matches", savings: "Reduces downstream Lambda cost", effort: "Low", description: "Overly broad EventBridge rules trigger Lambda for events you then filter in code. Move filtering to the rule pattern — pay only for relevant invocations.", action: "EventBridge → Rule → Event pattern → add detailed filters to match only needed events" },
        ],
      },
    ],
  },
  {
    id: "governance",
    label: "Governance",
    color: "#232F3E",
    Icon: IconGovernance,
    services: [
      {
        id: "cost-explorer",
        label: "Cost Explorer",
        tips: [
          { title: "Enable Cost Explorer", savings: "Enables all other savings", effort: "Low", description: "Free to enable. Shows spend by service, region, tag, linked account. Use it weekly to spot anomalies before they compound.", action: "Billing → Cost Explorer → Enable" },
          { title: "Set up Cost Anomaly Detection", savings: "Prevents unexpected spikes", effort: "Low", description: "AWS ML-powered anomaly detection alerts when spend deviates from predicted patterns. Catches runaway resources before month-end.", action: "Cost Explorer → Cost Anomaly Detection → Create monitor + SNS alert" },
          { title: "Create AWS Budgets", savings: "Prevents bill shock", effort: "Low", description: "Set budget thresholds at 50%, 80%, 100% of monthly target. Budget alerts via email or SNS. Also supports RI and Savings Plans coverage budgets.", action: "Billing → Budgets → Create budget → Cost budget + alert thresholds" },
        ],
      },
      {
        id: "tagging",
        label: "Tagging",
        tips: [
          { title: "Tag everything from day one", savings: "Enables full cost attribution", effort: "Medium", description: "Without tags you can't attribute cost to teams or products. Enforce: Project, Environment, Owner, CostCenter. Untagged resources become invisible waste.", action: "AWS Organizations → Tag policies → enforce required tags via SCP" },
          { title: "Use AWS Resource Groups", savings: "Visibility into team spend", effort: "Low", description: "Group resources by tag to get per-team cost views in Cost Explorer without a separate account per team.", action: "Resource Groups → Create group → Tag-based → align with Cost Explorer filters" },
        ],
      },
      {
        id: "trusted-advisor",
        label: "Trusted Advisor",
        tips: [
          { title: "Review Trusted Advisor weekly", savings: "Varies", effort: "Low", description: "Business/Enterprise support gives full access. Flags idle load balancers, underused RIs, unassociated EIPs, and over-provisioned resources.", action: "Trusted Advisor → Cost Optimization → review and action each check" },
          { title: "Review Compute Optimizer", savings: "20–70% on flagged resources", effort: "Low", description: "Free ML-powered recommendations for EC2, EBS, Lambda, ECS, and RDS based on 14-day utilization. Often surfaces savings Trusted Advisor misses.", action: "Compute Optimizer → Dashboard → Review all recommendation types" },
        ],
      },
      {
        id: "organizations",
        label: "Organizations",
        tips: [
          { title: "Consolidate billing for volume discounts", savings: "5–15% via aggregated usage", effort: "Low", description: "AWS aggregates usage across all accounts in an org for tiered pricing (S3, Data Transfer, etc.). Consolidating even 2 accounts unlocks better tiers.", action: "AWS Organizations → Create organization → invite member accounts" },
          { title: "Use SCPs to prevent expensive resource creation", savings: "Prevents accidental large spend", effort: "Medium", description: "Service Control Policies can deny creation of expensive instance types, regions you don't use, or services outside your approved list.", action: "Organizations → Policies → Service control policies → deny expensive resource types" },
        ],
      },
    ],
  },
];

const EFFORT_STYLES: Record<string, { bg: string; color: string }> = {
  Low:    { bg: 'rgba(34,197,94,0.12)',  color: '#14532d' },
  Medium: { bg: 'rgba(234,179,8,0.12)',  color: '#713f12' },
  High:   { bg: 'rgba(239,68,68,0.12)',  color: '#991b1b' },
};

export default function AWSCheatSheet() {
  const isMobile = useIsMobile();
  const [activeCategoryId, setActiveCategoryId] = useState(categories[0].id);
  const [activeServiceId, setActiveServiceId] = useState(categories[0].services[0].id);
  const [search, setSearch] = useState('');
  const [selectedTip, setSelectedTip] = useState<{ cat: typeof categories[0]; svc: Service; tip: Tip } | null>(null);

  const PLACEHOLDERS = ['Try "NAT Gateway"…', 'Try "Spot instances"…', 'Try "S3 lifecycle"…', 'Try "RDS rightsizing"…', 'Try "Lambda cold start"…', 'Try "Reserved vs On-demand"…'];
  const [placeholderText, setPlaceholderText] = useState('');
  const [, setPlaceholderIdx] = useState(0);
  const placeholderRef = useRef<{ idx: number; typing: boolean; charIdx: number }>({ idx: 0, typing: true, charIdx: 0 });

  useEffect(() => {
    let timeout: ReturnType<typeof setTimeout>;
    const tick = () => {
      const state = placeholderRef.current;
      const target = PLACEHOLDERS[state.idx];
      if (state.typing) {
        if (state.charIdx <= target.length) {
          setPlaceholderText(target.slice(0, state.charIdx));
          state.charIdx++;
          timeout = setTimeout(tick, 45);
        } else {
          state.typing = false;
          timeout = setTimeout(tick, 1800);
        }
      } else {
        if (state.charIdx > 0) {
          state.charIdx--;
          setPlaceholderText(target.slice(0, state.charIdx));
          timeout = setTimeout(tick, 25);
        } else {
          state.idx = (state.idx + 1) % PLACEHOLDERS.length;
          state.typing = true;
          setPlaceholderIdx(state.idx);
          timeout = setTimeout(tick, 300);
        }
      }
    };
    timeout = setTimeout(tick, 600);
    return () => clearTimeout(timeout);
  }, []);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setSelectedTip(null); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  const activeCategory = categories.find(c => c.id === activeCategoryId)!;
  const activeService = activeCategory.services.find(s => s.id === activeServiceId) ?? activeCategory.services[0];

  const totalTips = categories.reduce((a, c) => a + c.services.reduce((b, s) => b + s.tips.length, 0), 0);
  const totalServices = categories.reduce((a, c) => a + c.services.length, 0);

  const searchResults = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return null;
    const results: { cat: Category; svc: Service; tip: Tip }[] = [];
    for (const cat of categories) {
      for (const svc of cat.services) {
        for (const tip of svc.tips) {
          if (
            tip.title.toLowerCase().includes(q) ||
            tip.description.toLowerCase().includes(q) ||
            tip.action.toLowerCase().includes(q) ||
            svc.label.toLowerCase().includes(q) ||
            cat.label.toLowerCase().includes(q)
          ) {
            results.push({ cat, svc, tip });
          }
        }
      }
    }
    return results;
  }, [search]);

  function selectCategory(catId: string) {
    const cat = categories.find(c => c.id === catId)!;
    setActiveCategoryId(catId);
    setActiveServiceId(cat.services[0].id);
    setSearch('');
  }

  function selectService(svcId: string) {
    setActiveServiceId(svcId);
  }

  const PAGE_TITLE = 'Frugal Cloud Guide — AWS Cost Optimization Tactics | CloudFrugal';
  const PAGE_DESC = `${totalTips} real-world AWS cost-cutting tactics across ${totalServices} services. Includes effort level, estimated savings, and exact console actions. Free forever.`;
  const PAGE_URL = 'https://cloudfrugal.app/cheatsheet';
  const OG_IMAGE = 'https://cloudfrugal.app/og-cheatsheet.png';

  return (
    <div className="min-h-screen" style={{ background: '#F6F1E3', fontFamily: FONT }}>
      <Helmet>
        <title>{PAGE_TITLE}</title>
        <meta name="description" content={PAGE_DESC} />
        <link rel="canonical" href={PAGE_URL} />

        {/* Open Graph */}
        <meta property="og:type" content="website" />
        <meta property="og:url" content={PAGE_URL} />
        <meta property="og:title" content={PAGE_TITLE} />
        <meta property="og:description" content={PAGE_DESC} />
        <meta property="og:image" content={OG_IMAGE} />
        <meta property="og:site_name" content="CloudFrugal" />

        {/* Twitter Card */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={PAGE_TITLE} />
        <meta name="twitter:description" content={PAGE_DESC} />
        <meta name="twitter:image" content={OG_IMAGE} />

        {/* Structured data */}
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Article",
          "headline": "Frugal Cloud Guide — AWS Cost Optimization Tactics",
          "description": PAGE_DESC,
          "url": PAGE_URL,
          "author": { "@type": "Organization", "name": "CloudFrugal", "url": "https://cloudfrugal.app" },
          "publisher": { "@type": "Organization", "name": "CloudFrugal", "logo": { "@type": "ImageObject", "url": "https://cloudfrugal.app/favicon.svg" } },
        })}</script>
      </Helmet>

      {/* Hero — dark mesh */}
      <div style={{
        background: [
          `url("data:image/svg+xml,%3Csvg width='22' height='22' xmlns='http://www.w3.org/2000/svg'%3E%3Ccircle cx='1' cy='1' r='1.1' fill='%23ffffff' fill-opacity='0.03'/%3E%3C/svg%3E") repeat`,
          `radial-gradient(ellipse 640px 400px at 5% 55%, rgba(15,61,46,0.9) 0%, transparent 100%)`,
          `radial-gradient(ellipse 520px 340px at 95% 10%, rgba(232,184,76,0.12) 0%, transparent 100%)`,
          `radial-gradient(ellipse 460px 360px at 60% 110%, rgba(107,116,184,0.25) 0%, transparent 100%)`,
          `rgb(7,40,29)`,
        ].join(', '),
        borderBottom: '1px solid rgba(255,255,255,0.06)',
      }}>
        <div style={{ maxWidth: '860px', margin: '0 auto', padding: isMobile ? '20px 16px 16px' : '28px 24px 20px', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '14px' }}>

          {/* Visually-hidden h1 for SEO */}
          <h1 style={{ position: 'absolute', width: '1px', height: '1px', padding: 0, margin: '-1px', overflow: 'hidden', clip: 'rect(0,0,0,0)', whiteSpace: 'nowrap', border: 0 }}>
            Frugal Cloud Guide — AWS Cost Optimization Tactics
          </h1>

          {/* Description */}
          <div style={{ textAlign: 'center' }}>
            <p style={{ fontSize: '11px', fontWeight: 700, letterSpacing: '0.12em', textTransform: 'uppercase', color: '#E8B84C', fontFamily: FONT, margin: '0 0 10px' }}>
              Frugal Cloud Guide
            </p>
            <p style={{ fontSize: 'clamp(20px, 3.5vw, 32px)', fontWeight: 800, color: '#ffffff', fontFamily: FONT, margin: '0 0 8px', lineHeight: 1.2, letterSpacing: '-0.02em' }}>
              Actionable playbooks to reduce your AWS bill.
            </p>
            <p style={{ fontSize: isMobile ? '14px' : '15px', color: 'rgba(255,255,255,0.55)', fontFamily: FONT, margin: '0 0 14px', lineHeight: 1.6 }}>
              {totalTips} tactics across {totalServices} AWS services — with effort levels, savings estimates, and next steps.
            </p>
          </div>

          {/* Search + Share inline */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', width: '100%', maxWidth: '540px' }}>
            <div style={{ position: 'relative', flex: 1 }}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
                style={{ position: 'absolute', left: '14px', top: '50%', transform: 'translateY(-50%)', color: 'rgba(255,255,255,0.3)', pointerEvents: 'none' }}>
                <circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" />
              </svg>
              <input
                type="text"
                placeholder={search ? '' : (placeholderText || 'Search tactics…')}
                value={search}
                onChange={e => { setSearch(e.target.value); }}
                style={{ width: '100%', background: 'rgba(255,255,255,0.07)', border: '1px solid rgba(255,255,255,0.12)', borderRadius: '10px', padding: '10px 14px 10px 40px', color: '#ffffff', fontSize: '14px', fontFamily: FONT, outline: 'none', boxSizing: 'border-box' }}
                onFocus={e => (e.target.style.borderColor = '#E8B84C')}
                onBlur={e => (e.target.style.borderColor = 'rgba(255,255,255,0.12)')}
              />
              {search && (
                <button onClick={() => setSearch('')} style={{ position: 'absolute', right: '12px', top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', color: 'rgba(255,255,255,0.4)', cursor: 'pointer', fontSize: '18px', lineHeight: 1, padding: 0 }}>×</button>
              )}
            </div>
            <ShareButtons url="https://cloudfrugal.app/cheatsheet" title="The Frugal Cloud Guide — AWS cost optimization tactics" theme="light" />
          </div>
        </div>
      </div>

      {/* Mobile category scroll row */}
      {isMobile && (
        <div style={{ background: '#ffffff', borderBottom: '1px solid rgba(15,61,46,0.08)', padding: '10px 0' }}>
          <div style={{ display: 'flex', gap: '6px', overflowX: 'auto', scrollbarWidth: 'none', padding: '0 16px' }}>
            {categories.map(cat => {
              const isActive = cat.id === activeCategoryId && !search;
              return (
                <button
                  key={cat.id}
                  onClick={() => selectCategory(cat.id)}
                  style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', flexShrink: 0, padding: '6px 12px', borderRadius: '99px', background: isActive ? 'rgb(7,40,29)' : 'rgba(15,61,46,0.05)', border: isActive ? '1px solid #E8B84C' : '1px solid rgba(15,61,46,0.12)', color: isActive ? '#ffffff' : 'rgba(15,61,46,0.6)', fontSize: '12px', fontWeight: isActive ? 800 : 600, fontFamily: FONT, cursor: 'pointer', transition: 'all 0.15s' }}
                >
                  <span style={{ color: isActive ? '#E8B84C' : 'rgba(15,61,46,0.4)', display: 'flex' }}><cat.Icon /></span>
                  <span>{cat.label}</span>
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* Main layout */}
      <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '220px 1fr', minHeight: 'calc(100vh - 200px)' }}>

        {/* Sidebar (desktop only) */}
        {!isMobile && (
        <aside style={{
          background: [
            `url("data:image/svg+xml,%3Csvg width='22' height='22' xmlns='http://www.w3.org/2000/svg'%3E%3Ccircle cx='1' cy='1' r='1.1' fill='%231E2A6B' fill-opacity='0.035'/%3E%3C/svg%3E") repeat`,
            `radial-gradient(ellipse 280px 320px at 50% 105%, rgba(232,184,76,0.2) 0%, transparent 100%)`,
            `radial-gradient(ellipse 200px 240px at 110% 88%, rgba(232,184,76,0.1) 0%, transparent 100%)`,
            `radial-gradient(ellipse 220px 300px at -20% 70%, rgba(15,61,46,0.06) 0%, transparent 100%)`,
            `#ffffff`,
          ].join(', '),
          borderRight: '1px solid rgba(15,61,46,0.08)',
          padding: '24px 0',
        }}>
          <p style={{ fontSize: '11px', fontWeight: 800, letterSpacing: '0.1em', textTransform: 'uppercase', color: 'rgba(15,61,46,0.55)', padding: '0 20px', marginBottom: '12px', fontFamily: FONT }}>Categories</p>
          {categories.map(cat => {
            const isActive = cat.id === activeCategoryId && !search;
            return (
              <button
                key={cat.id}
                onClick={() => selectCategory(cat.id)}
                style={{ display: 'flex', alignItems: 'center', gap: '10px', width: '100%', padding: '9px 20px', background: isActive ? 'rgb(7,40,29)' : 'none', border: 'none', borderLeft: isActive ? '3px solid #E8B84C' : '3px solid transparent', color: isActive ? '#ffffff' : 'rgba(15,61,46,0.6)', fontSize: '13px', fontWeight: isActive ? 800 : 700, fontFamily: FONT, cursor: 'pointer', textAlign: 'left', transition: 'all 0.15s', borderRadius: isActive ? '0 8px 8px 0' : '0' }}
                onMouseEnter={e => { if (!isActive) { (e.currentTarget as HTMLButtonElement).style.color = '#0F3D2E'; (e.currentTarget as HTMLButtonElement).style.background = 'rgba(15,61,46,0.05)'; } }}
                onMouseLeave={e => { if (!isActive) { (e.currentTarget as HTMLButtonElement).style.color = 'rgba(15,61,46,0.6)'; (e.currentTarget as HTMLButtonElement).style.background = 'none'; } }}
              >
                <span style={{ color: isActive ? '#E8B84C' : 'rgba(15,61,46,0.4)', flexShrink: 0 }}><cat.Icon /></span>
                <span>{cat.label}</span>
                <span style={{ marginLeft: 'auto', fontSize: '11px', fontWeight: 600, color: 'rgba(15,61,46,0.35)' }}>{cat.services.reduce((a, s) => a + s.tips.length, 0)}</span>
              </button>
            );
          })}
        </aside>
        )}

        {/* Content */}
        <div style={{ minWidth: 0 }}>

          {/* Search results */}
          {searchResults !== null && (
            <div style={{ padding: isMobile ? '16px 16px 32px' : '32px 32px 48px', maxWidth: '1200px' }}>
              <p style={{ fontSize: '13px', color: 'rgba(15,22,32,0.4)', marginBottom: '24px', fontFamily: FONT }}>
                {searchResults.length === 0 ? `No results for "${search}"` : `${searchResults.length} result${searchResults.length !== 1 ? 's' : ''} for "${search}"`}
              </p>
              <div style={{ display: 'grid', gap: '16px', gridTemplateColumns: `repeat(auto-fill, minmax(${isMobile ? '260px' : '300px'}, 1fr))` }}>
                {searchResults.map(({ cat, svc, tip }, i) => {
                  const effort = EFFORT_STYLES[tip.effort] ?? EFFORT_STYLES.Medium;
                  return (
                    <TipCard key={i} tip={tip} cat={cat} svc={svc} effort={effort} onClick={() => setSelectedTip({ cat, svc, tip })} showBreadcrumb />
                  );
                })}
              </div>
            </div>
          )}

          {/* Browse view */}
          {!searchResults && (
            <>
              <div style={{ borderBottom: '1px solid rgba(15,61,46,0.1)', background: '#F6F1E3', padding: isMobile ? '0 16px' : '0 32px', maxWidth: '1200px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', overflowX: 'auto', scrollbarWidth: 'none', padding: '14px 0' }}>
                  <span style={{ fontSize: '11px', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'rgba(15,22,32,0.3)', flexShrink: 0, marginRight: '4px', fontFamily: FONT }}>{activeCategory.label}:</span>
                  {activeCategory.services.map(svc => {
                    const isActive = svc.id === activeService.id;
                    return (
                      <button key={svc.id} onClick={() => selectService(svc.id)}
                        style={{ padding: '5px 14px', borderRadius: '99px', background: isActive ? activeCategory.color : 'white', color: isActive ? '#fff' : 'rgba(15,22,32,0.55)', border: isActive ? `1px solid ${activeCategory.color}` : '1px solid rgba(15,61,46,0.15)', fontSize: '13px', fontWeight: 600, fontFamily: FONT, cursor: 'pointer', flexShrink: 0, transition: 'all 0.15s' }}>
                        {svc.label}
                        <span style={{ marginLeft: '6px', fontSize: '11px', opacity: 0.6 }}>{svc.tips.length}</span>
                      </button>
                    );
                  })}
                </div>
              </div>

              <div style={{ padding: isMobile ? '16px 16px 32px' : '32px 32px 48px', maxWidth: '1200px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '24px' }}>
                  <div style={{ width: '40px', height: '40px', borderRadius: '12px', display: 'flex', alignItems: 'center', justifyContent: 'center', background: `${activeCategory.color}18`, color: activeCategory.color, flexShrink: 0 }}>
                    <activeCategory.Icon />
                  </div>
                  <div>
                    <h2 style={{ fontSize: '18px', fontWeight: 700, color: 'rgb(22,30,45)', fontFamily: FONT, margin: 0 }}>{activeService.label}</h2>
                    <p style={{ fontSize: '13px', color: 'rgba(15,22,32,0.4)', fontFamily: FONT, margin: 0 }}>{activeCategory.label} · {activeService.tips.length} tip{activeService.tips.length !== 1 ? 's' : ''}</p>
                  </div>
                </div>

                <div style={{ display: 'grid', gap: '16px', gridTemplateColumns: `repeat(auto-fill, minmax(${isMobile ? '260px' : '300px'}, 1fr))` }}>
                  {activeService.tips.map((tip, i) => {
                    const effort = EFFORT_STYLES[tip.effort] ?? EFFORT_STYLES.Medium;
                    return (
                      <TipCard key={i} tip={tip} cat={activeCategory} svc={activeService} effort={effort} onClick={() => setSelectedTip({ cat: activeCategory, svc: activeService, tip })} />
                    );
                  })}
                </div>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Tip overlay modal */}
      {selectedTip && (
        <TipModal tip={selectedTip.tip} cat={selectedTip.cat} svc={selectedTip.svc} onClose={() => setSelectedTip(null)} />
      )}

      <Footer />
    </div>
  );
}

// ─── Tip Card ────────────────────────────────────────────────────────────────
interface TipCardProps {
  tip: Tip;
  cat: Category;
  svc: Service;
  effort: { bg: string; color: string };
  onClick: () => void;
  showBreadcrumb?: boolean;
}

function TipCard({ tip, cat, svc, effort, onClick, showBreadcrumb }: TipCardProps) {
  const [hovered, setHovered] = React.useState(false);
  return (
    <button
      onClick={onClick}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        display: 'block', width: '100%', textAlign: 'left',
        background: 'white', borderRadius: '16px', overflow: 'hidden',
        border: hovered ? `1px solid ${cat.color}55` : '1px solid rgba(15,61,46,0.1)',
        boxShadow: hovered ? `0 8px 28px rgba(14,18,48,0.1)` : '0 1px 4px rgba(14,18,48,0.04)',
        cursor: 'pointer', fontFamily: FONT, padding: 0,
        transform: hovered ? 'translateY(-2px)' : 'translateY(0)',
        transition: 'border-color 0.2s, box-shadow 0.2s, transform 0.2s',
        alignSelf: 'start',
      }}
    >
      {showBreadcrumb && (
        <div style={{ fontSize: '11px', fontWeight: 600, padding: '7px 16px', background: `${cat.color}14`, color: cat.color, fontFamily: FONT }}>
          {cat.label} · {svc.label}
        </div>
      )}
      <div style={{ padding: '18px 20px' }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '12px', marginBottom: '12px' }}>
          <p style={{ fontSize: '15px', fontWeight: 700, color: 'rgb(22,30,45)', margin: 0, lineHeight: 1.4 }}>{tip.title}</p>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={cat.color} strokeWidth="2.5" strokeLinecap="round" style={{ flexShrink: 0, marginTop: '2px', opacity: hovered ? 1 : 0.4, transition: 'opacity 0.2s' }}>
            <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" /><polyline points="15 3 21 3 21 9" /><line x1="10" y1="14" x2="21" y2="3" />
          </svg>
        </div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
          <span style={{ fontSize: '12px', fontWeight: 700, padding: '3px 10px', borderRadius: '99px', background: 'rgba(232,184,76,0.2)', color: '#3a5c00', fontFamily: FONT }}>💰 {tip.savings}</span>
          <span style={{ fontSize: '12px', fontWeight: 600, padding: '3px 10px', borderRadius: '99px', ...effort, fontFamily: FONT }}>Effort: {tip.effort}</span>
        </div>
        <p style={{ fontSize: '13px', lineHeight: 1.6, color: 'rgba(15,22,32,0.5)', margin: '10px 0 0', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
          {tip.description}
        </p>
        {tip.awsDocsUrl && (
          <div style={{ marginTop: '10px', display: 'flex', alignItems: 'center', gap: '4px' }}>
            <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="rgba(15,61,46,0.35)" strokeWidth="2.5" strokeLinecap="round"><path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/><polyline points="15 3 21 3 21 9"/><line x1="10" y1="14" x2="21" y2="3"/></svg>
            <span style={{ fontSize: '11px', fontWeight: 600, color: 'rgba(15,61,46,0.4)', fontFamily: FONT }}>AWS Docs</span>
          </div>
        )}
      </div>
    </button>
  );
}

// ─── Tip Modal ────────────────────────────────────────────────────────────────
interface TipModalProps {
  tip: Tip;
  cat: Category;
  svc: Service;
  onClose: () => void;
}

function TipModal({ tip, cat, svc, onClose }: TipModalProps) {
  const isMobile = useIsMobile();
  const effort = EFFORT_STYLES[tip.effort] ?? EFFORT_STYLES.Medium;
  const [copied, setCopied] = React.useState(false);

  // Parse action into console navigation steps (split on " → ")
  const actionSteps = tip.action.split(' → ');
  const isNavPath = actionSteps.length > 1;

  // Derive risk from effort
  const RISK = { Low: { label: 'Low risk', color: '#14532d', bg: 'rgba(34,197,94,0.1)' }, Medium: { label: 'Moderate risk', color: '#713f12', bg: 'rgba(234,179,8,0.1)' }, High: { label: 'Higher risk', color: '#991b1b', bg: 'rgba(239,68,68,0.1)' } };
  const risk = RISK[tip.effort as keyof typeof RISK] ?? RISK.Medium;

  // Derive savings magnitude for visual bar (1–5)
  function savingsMagnitude(): number {
    const s = tip.savings.toLowerCase();
    if (s.includes('100%') || s.includes('eliminates')) return 5;
    if (s.includes('90') || s.includes('80')) return 4;
    if (s.includes('60') || s.includes('70') || s.includes('64')) return 3;
    if (s.includes('30') || s.includes('40') || s.includes('50')) return 2;
    return 1;
  }
  const savingsBars = savingsMagnitude();

  // Related tips from same service (exclude current)
  const relatedTips = svc.tips.filter(t => t.title !== tip.title).slice(0, 2);

  function copyAction() {
    navigator.clipboard.writeText(tip.action).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  }

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, zIndex: 100,
        background: 'rgba(14,18,48,0.55)',
        backdropFilter: 'blur(4px)',
        WebkitBackdropFilter: 'blur(4px)',
        display: 'flex', alignItems: isMobile ? 'flex-end' : 'center', justifyContent: 'center',
        padding: isMobile ? '0' : '24px',
        animation: 'cf-fade-in 0.18s ease',
      }}
    >
      <div
        onClick={e => e.stopPropagation()}
        style={{
          background: '#fff', borderRadius: isMobile ? '20px 20px 0 0' : '20px', width: '100%', maxWidth: isMobile ? '100%' : '600px',
          maxHeight: isMobile ? '92vh' : '88vh', overflowY: 'auto',
          boxShadow: '0 24px 80px rgba(14,18,48,0.25), 0 4px 16px rgba(14,18,48,0.1)',
          animation: 'cf-slide-up 0.22s cubic-bezier(0.34,1.56,0.64,1)',
          fontFamily: FONT,
        }}
      >
        {/* Drag handle (mobile) */}
        {isMobile && (
          <div style={{ display: 'flex', justifyContent: 'center', padding: '10px 0 4px' }}>
            <div style={{ width: '36px', height: '4px', borderRadius: '2px', background: 'rgba(15,61,46,0.15)' }} />
          </div>
        )}

        {/* Header */}
        <div style={{ background: `linear-gradient(135deg, ${cat.color}22 0%, ${cat.color}08 100%)`, borderBottom: `1px solid ${cat.color}22`, padding: isMobile ? '12px 16px 12px' : '20px 24px 18px', display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '12px' }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '10px', flexWrap: 'wrap' }}>
              <span style={{ color: cat.color, display: 'flex' }}><cat.Icon /></span>
              <span style={{ fontSize: '12px', fontWeight: 700, color: cat.color }}>{cat.label}</span>
              <span style={{ fontSize: '12px', color: 'rgba(15,61,46,0.3)' }}>›</span>
              <span style={{ fontSize: '12px', fontWeight: 600, color: 'rgba(15,61,46,0.5)' }}>{svc.label}</span>
            </div>
            <h3 style={{ fontSize: isMobile ? '17px' : '20px', fontWeight: 800, color: 'rgb(7,40,29)', margin: 0, lineHeight: 1.3, fontFamily: FONT }}>{tip.title}</h3>
          </div>
          <button onClick={onClose}
            style={{ background: 'rgba(15,61,46,0.08)', border: 'none', borderRadius: '50%', width: '32px', height: '32px', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', flexShrink: 0, color: 'rgba(14,18,48,0.5)', fontSize: '18px', lineHeight: 1 }}
            onMouseEnter={e => (e.currentTarget.style.background = 'rgba(15,61,46,0.15)')}
            onMouseLeave={e => (e.currentTarget.style.background = 'rgba(15,61,46,0.08)')}
          >×</button>
        </div>

        {/* Stats row */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', borderBottom: '1px solid rgba(15,61,46,0.08)' }}>
          {/* Savings */}
          <div style={{ padding: isMobile ? '12px' : '16px 20px', borderRight: '1px solid rgba(15,61,46,0.08)' }}>
            <p style={{ fontSize: '10px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.1em', color: 'rgba(15,61,46,0.4)', margin: '0 0 6px', fontFamily: FONT }}>Savings</p>
            <div style={{ display: 'flex', gap: '3px', marginBottom: '5px' }}>
              {[1,2,3,4,5].map(n => (
                <div key={n} style={{ width: '16px', height: '6px', borderRadius: '3px', background: n <= savingsBars ? '#E8B84C' : 'rgba(15,61,46,0.1)' }} />
              ))}
            </div>
            <p style={{ fontSize: '12px', fontWeight: 700, color: '#3a5c00', margin: 0, fontFamily: FONT }}>{tip.savings}</p>
          </div>
          {/* Effort */}
          <div style={{ padding: isMobile ? '12px' : '16px 20px', borderRight: '1px solid rgba(15,61,46,0.08)' }}>
            <p style={{ fontSize: '10px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.1em', color: 'rgba(15,61,46,0.4)', margin: '0 0 6px', fontFamily: FONT }}>Effort</p>
            <span style={{ fontSize: '12px', fontWeight: 700, padding: '3px 10px', borderRadius: '99px', ...effort, fontFamily: FONT }}>{tip.effort}</span>
          </div>
          {/* Risk */}
          <div style={{ padding: isMobile ? '12px' : '16px 20px' }}>
            <p style={{ fontSize: '10px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.1em', color: 'rgba(15,61,46,0.4)', margin: '0 0 6px', fontFamily: FONT }}>Risk</p>
            <span style={{ fontSize: '12px', fontWeight: 700, padding: '3px 10px', borderRadius: '99px', background: risk.bg, color: risk.color, fontFamily: FONT }}>{risk.label}</span>
          </div>
        </div>

        {/* Body */}
        <div style={{ padding: isMobile ? '16px' : '24px' }}>

          {/* Why this matters */}
          <p style={{ fontSize: '11px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.1em', color: 'rgba(15,61,46,0.4)', margin: '0 0 10px', fontFamily: FONT }}>Why this matters</p>
          <p style={{ fontSize: '15px', lineHeight: 1.75, color: 'rgba(14,18,48,0.75)', margin: '0 0 28px', fontFamily: FONT }}>{tip.description}</p>

          {/* How to do it */}
          <p style={{ fontSize: '11px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.1em', color: 'rgba(15,61,46,0.4)', margin: '0 0 12px', fontFamily: FONT }}>How to do it</p>

          {isNavPath ? (
            /* Console navigation breadcrumb steps */
            <div style={{ borderRadius: '14px', border: '1px solid rgba(15,61,46,0.1)', overflow: 'hidden', marginBottom: '8px' }}>
              <div style={{ background: 'rgba(15,61,46,0.03)', borderBottom: '1px solid rgba(15,61,46,0.08)', padding: '8px 16px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                <div style={{ width: '8px', height: '8px', borderRadius: '50%', background: '#ef4444' }} />
                <div style={{ width: '8px', height: '8px', borderRadius: '50%', background: '#f59e0b' }} />
                <div style={{ width: '8px', height: '8px', borderRadius: '50%', background: '#22c55e' }} />
                <span style={{ marginLeft: '6px', fontSize: '11px', color: 'rgba(15,61,46,0.35)', fontFamily: FONT }}>AWS Console</span>
              </div>
              <div style={{ padding: '16px', display: 'flex', flexWrap: 'wrap', gap: '6px', alignItems: 'center' }}>
                {actionSteps.map((step, i) => (
                  <React.Fragment key={i}>
                    <span style={{ fontSize: '13px', fontWeight: 600, padding: '4px 12px', borderRadius: '8px', background: i === actionSteps.length - 1 ? `${cat.color}20` : 'rgba(15,61,46,0.06)', color: i === actionSteps.length - 1 ? cat.color : 'rgb(22,30,45)', border: i === actionSteps.length - 1 ? `1px solid ${cat.color}40` : '1px solid rgba(15,61,46,0.1)', fontFamily: FONT }}>
                      {step.trim()}
                    </span>
                    {i < actionSteps.length - 1 && (
                      <span style={{ color: 'rgba(15,61,46,0.25)', fontSize: '14px' }}>›</span>
                    )}
                  </React.Fragment>
                ))}
              </div>
            </div>
          ) : (
            /* Free-text action block */
            <div style={{ borderRadius: '14px', background: 'rgb(7,40,29)', padding: '16px 20px', marginBottom: '8px', position: 'relative' }}>
              <div style={{ display: 'flex', gap: '10px', alignItems: 'flex-start', paddingRight: '60px' }}>
                <span style={{ fontSize: '13px', fontWeight: 800, color: '#E8B84C', flexShrink: 0, marginTop: '1px' }}>▶</span>
                <p style={{ fontSize: '14px', lineHeight: 1.7, color: 'rgba(255,255,255,0.82)', margin: 0, fontFamily: FONT }}>{tip.action}</p>
              </div>
            </div>
          )}

          {/* Copy button below the block */}
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '28px' }}>
            <button onClick={copyAction}
              style={{ display: 'flex', alignItems: 'center', gap: '6px', background: copied ? 'rgba(232,184,76,0.15)' : 'rgba(15,61,46,0.06)', border: `1px solid ${copied ? 'rgba(232,184,76,0.4)' : 'rgba(15,61,46,0.12)'}`, borderRadius: '8px', padding: '5px 14px', fontSize: '12px', fontWeight: 600, color: copied ? '#3a5c00' : 'rgba(15,61,46,0.6)', cursor: 'pointer', fontFamily: FONT, transition: 'all 0.2s' }}>
              {copied ? '✓ Copied to clipboard' : '⎘ Copy steps'}
            </button>
          </div>

          {/* AWS Docs link */}
          {tip.awsDocsUrl && (
            <div style={{ marginBottom: '28px' }}>
              <p style={{ fontSize: '11px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.1em', color: 'rgba(15,61,46,0.4)', margin: '0 0 10px', fontFamily: FONT }}>AWS Documentation</p>
              <a
                href={tip.awsDocsUrl}
                target="_blank"
                rel="noopener noreferrer"
                style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', padding: '10px 16px', borderRadius: '10px', border: '1px solid rgba(15,61,46,0.15)', background: 'rgba(15,61,46,0.03)', textDecoration: 'none', fontFamily: FONT, transition: 'all 0.15s' }}
                onMouseEnter={e => { (e.currentTarget as HTMLAnchorElement).style.background = 'rgba(15,61,46,0.07)'; (e.currentTarget as HTMLAnchorElement).style.borderColor = 'rgba(15,61,46,0.3)'; }}
                onMouseLeave={e => { (e.currentTarget as HTMLAnchorElement).style.background = 'rgba(15,61,46,0.03)'; (e.currentTarget as HTMLAnchorElement).style.borderColor = 'rgba(15,61,46,0.15)'; }}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#0F3D2E" strokeWidth="2" strokeLinecap="round"><path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/><polyline points="15 3 21 3 21 9"/><line x1="10" y1="14" x2="21" y2="3"/></svg>
                <span style={{ fontSize: '13px', fontWeight: 600, color: '#0F3D2E' }}>
                  {tip.awsDocsUrl.replace('https://docs.aws.amazon.com/', 'docs.aws.amazon.com/')}
                </span>
              </a>
            </div>
          )}

          {/* Related tips */}
          {relatedTips.length > 0 && (
            <>
              <p style={{ fontSize: '11px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.1em', color: 'rgba(15,61,46,0.4)', margin: '0 0 12px', fontFamily: FONT }}>More {svc.label} tips</p>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                {relatedTips.map((rt, i) => {
                  const rtEffort = EFFORT_STYLES[rt.effort] ?? EFFORT_STYLES.Medium;
                  return (
                    <div key={i} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px', padding: '12px 16px', borderRadius: '12px', border: '1px solid rgba(15,61,46,0.1)', background: 'rgba(15,61,46,0.02)' }}>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <p style={{ fontSize: '13px', fontWeight: 700, color: 'rgb(22,30,45)', margin: '0 0 4px', fontFamily: FONT }}>{rt.title}</p>
                        <div style={{ display: 'flex', gap: '6px' }}>
                          <span style={{ fontSize: '11px', fontWeight: 700, padding: '2px 8px', borderRadius: '99px', background: 'rgba(232,184,76,0.18)', color: '#3a5c00', fontFamily: FONT }}>💰 {rt.savings}</span>
                          <span style={{ fontSize: '11px', fontWeight: 600, padding: '2px 8px', borderRadius: '99px', ...rtEffort, fontFamily: FONT }}>{rt.effort}</span>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </div>

        {/* Footer */}
        <div style={{ borderTop: '1px solid rgba(15,61,46,0.08)', padding: isMobile ? '12px 16px' : '14px 24px', display: 'flex', justifyContent: 'flex-end' }}>
          <button onClick={onClose}
            style={{ padding: '8px 20px', borderRadius: '10px', background: 'rgb(7,40,29)', color: '#fff', border: 'none', fontSize: '13px', fontWeight: 600, cursor: 'pointer', fontFamily: FONT }}
            onMouseEnter={e => (e.currentTarget.style.background = '#0F3D2E')}
            onMouseLeave={e => (e.currentTarget.style.background = 'rgb(7,40,29)')}
          >Done</button>
        </div>
      </div>

      <style>{`
        @keyframes cf-fade-in { from { opacity: 0 } to { opacity: 1 } }
        @keyframes cf-slide-up { from { opacity: 0; transform: translateY(16px) scale(0.97) } to { opacity: 1; transform: translateY(0) scale(1) } }
      `}</style>
    </div>
  );
}
